import { Box, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import BigNumber from 'bignumber.js';
import React, { useEffect, useRef } from 'react';

import type { HomeStatsWidgetId } from 'types/homepage';
import type { TimeChartData } from 'ui/shared/chart/types';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import { WEI } from 'lib/consts';
import { HOMEPAGE_STATS, HOMEPAGE_STATS_MICROSERVICE } from 'stubs/stats';
import GasInfoTooltip from 'ui/shared/gas/GasInfoTooltip';
import GasPrice from 'ui/shared/gas/GasPrice';
import IconSvg from 'ui/shared/IconSvg';
import type { Props as StatsWidgetProps } from 'ui/shared/stats/StatsWidget';
import StatsWidget from 'ui/shared/stats/StatsWidget';
import Hint from 'ui/shared/Hint';
import ContentLoader from 'ui/shared/ContentLoader';
import DataFetchAlert from 'ui/shared/DataFetchAlert';
import ChartArea from 'ui/shared/chart/ChartArea';
import ChartLine from 'ui/shared/chart/ChartLine';
import ChartOverlay from 'ui/shared/chart/ChartOverlay';
import ChartTooltip from 'ui/shared/chart/ChartTooltip';
import useTimeChartController from 'ui/shared/chart/useTimeChartController';

const rollupFeature = config.features.rollup;
const isStatsFeatureEnabled = config.features.stats.isEnabled;
const AUTO_REFRESH_INTERVAL = 5000; // 5 seconds

// 图表内容组件
const DailyTxChartContent = ({ data }: { data: TimeChartData }) => {
  const overlayRef = React.useRef<SVGRectElement>(null);

  const CHART_MARGIN = { bottom: 5, left: 10, right: 10, top: 5 };

  const axesConfig = React.useMemo(() => {
    return {
      x: { ticks: 0 },
      y: { ticks: 0, nice: true, noLabel: true },
    };
  }, []);

  const { rect, ref, axes, innerWidth, innerHeight, chartMargin } = useTimeChartController({
    data,
    margin: CHART_MARGIN,
    axesConfig,
  });

  return (
    <svg width="100%" height="100%" ref={ref} cursor="pointer">
      <g transform={`translate(${chartMargin.left || 0},${chartMargin.top || 0})`} opacity={rect ? 1 : 0}>
        <ChartArea
          data={data[0].items}
          xScale={axes?.x?.scale}
          yScale={axes?.y?.scale}
          fill="rgba(49, 130, 206, 0.05)"
        />
        <ChartLine
          data={data[0].items}
          xScale={axes?.x?.scale}
          yScale={axes?.y?.scale}
          stroke="#3182ce"
          animation="left"
          strokeWidth={1.5}
        />
        <ChartOverlay ref={overlayRef} width={innerWidth || 0} height={innerHeight || 0}>
          <ChartTooltip
            anchorEl={overlayRef.current}
            width={innerWidth || 0}
            height={innerHeight || 0}
            xScale={axes?.x?.scale}
            yScale={axes?.y?.scale}
            data={data}
          />
        </ChartOverlay>
      </g>
    </svg>
  );
};

// 图表容器组件
const DailyTxChartContainer = ({ data, isError, isPending }: { data: TimeChartData; isError: boolean; isPending: boolean }) => {
  if (isPending) {
    return <ContentLoader mt="auto" fontSize="xs" />;
  }

  if (isError) {
    return <DataFetchAlert fontSize="xs" />;
  }

  if (!data[0].items || data[0].items.length === 0) {
    return <Text fontSize="xs">No data available</Text>;
  }

  return (
    <Box mx="-10px" my="-5px" h="calc(100% + 10px)" w="calc(100% + 20px)">
      <DailyTxChartContent data={data} />
    </Box>
  );
};

// 辅助函数：准备图表数据
const prepareChartItems = (items: Array<any> = []) => {
  return items
    .map(item => ({ ...item, value: Number(item.value) }))
    .filter(item => !isNaN(item.value));
};

const Stats = () => {
  const [hasGasTracker, setHasGasTracker] = React.useState(config.features.gasTracker.isEnabled);
  const [isPageVisible, setIsPageVisible] = React.useState(true);

  // data from stats microservice is prioritized over data from stats api
  const statsQuery = useApiQuery('stats_main', {
    queryOptions: {
      refetchOnMount: false,
      placeholderData: isStatsFeatureEnabled ? HOMEPAGE_STATS_MICROSERVICE : undefined,
      enabled: isStatsFeatureEnabled && isPageVisible,
      refetchInterval: isPageVisible ? AUTO_REFRESH_INTERVAL : false,
    },
  });

  const apiQuery = useApiQuery('stats', {
    queryOptions: {
      refetchOnMount: false,
      placeholderData: HOMEPAGE_STATS,
      enabled: isPageVisible,
      refetchInterval: isPageVisible ? AUTO_REFRESH_INTERVAL : false,
    },
  });

  // 处理页面可见性变化
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const isPlaceholderData = statsQuery.isPlaceholderData || apiQuery.isPlaceholderData;

  React.useEffect(() => {
    if (!isPlaceholderData && !apiQuery.data?.gas_prices?.average) {
      setHasGasTracker(false);
    }
  }, [isPlaceholderData, apiQuery.data?.gas_prices?.average]);

  const zkEvmLatestBatchQuery = useApiQuery('homepage_zkevm_latest_batch', {
    queryOptions: {
      placeholderData: 12345,
      enabled: rollupFeature.isEnabled && rollupFeature.type === 'zkEvm' && config.UI.homepage.stats.includes('latest_batch'),
    },
  });

  const zkSyncLatestBatchQuery = useApiQuery('homepage_zksync_latest_batch', {
    queryOptions: {
      placeholderData: 12345,
      enabled: rollupFeature.isEnabled && rollupFeature.type === 'zkSync' && config.UI.homepage.stats.includes('latest_batch'),
    },
  });

  const arbitrumLatestBatchQuery = useApiQuery('homepage_arbitrum_latest_batch', {
    queryOptions: {
      placeholderData: 12345,
      enabled: rollupFeature.isEnabled && rollupFeature.type === 'arbitrum' && config.UI.homepage.stats.includes('latest_batch'),
    },
  });

  const latestBatchQuery = (() => {
    if (!rollupFeature.isEnabled || !config.UI.homepage.stats.includes('latest_batch')) {
      return;
    }

    switch (rollupFeature.type) {
      case 'zkEvm':
        return zkEvmLatestBatchQuery;
      case 'zkSync':
        return zkSyncLatestBatchQuery;
      case 'arbitrum':
        return arbitrumLatestBatchQuery;
    }
  })();

  if (apiQuery.isError || statsQuery.isError || latestBatchQuery?.isError) {
    return null;
  }

  const isLoading = isPlaceholderData || latestBatchQuery?.isPlaceholderData;

  interface Item extends StatsWidgetProps {
    id: HomeStatsWidgetId;
  }

  const apiData = apiQuery.data;
  const statsData = statsQuery.data;

  const items: Array<Item> = (() => {
    if (!statsData && !apiData) {
      return [];
    }

    const gasInfoTooltip = hasGasTracker && apiData?.gas_prices && apiData.gas_prices.average ? (
      <GasInfoTooltip data={apiData} dataUpdatedAt={apiQuery.dataUpdatedAt}>
        <IconSvg
          isLoading={isLoading}
          name="info"
          boxSize={5}
          flexShrink={0}
          cursor="pointer"
          color="icon.info"
          _hover={{ color: 'link.primary.hove' }}
        />
      </GasInfoTooltip>
    ) : null;

    return [
      latestBatchQuery?.data !== undefined && {
        id: 'latest_batch' as const,
        icon: 'txn_batches_slim' as const,
        label: 'Latest batch',
        value: latestBatchQuery.data.toLocaleString(),
        href: { pathname: '/batches' as const },
        isLoading,
      },
      (statsData?.total_blocks?.value || apiData?.total_blocks) && {
        id: 'total_blocks' as const,
        icon: 'block_slim' as const,
        label: statsData?.total_blocks?.title || 'Total blocks',
        value: Number(statsData?.total_blocks?.value || apiData?.total_blocks).toLocaleString(),
        href: { pathname: '/blocks' as const },
        isLoading,
      },
      (statsData?.average_block_time?.value || apiData?.average_block_time) && {
        id: 'average_block_time' as const,
        icon: 'clock-light' as const,
        label: statsData?.average_block_time?.title || 'Average block time',
        value: `${statsData?.average_block_time?.value ?
            Number(statsData.average_block_time.value).toFixed(1) :
            (apiData!.average_block_time / 1000).toFixed(1)
          }s`,
        isLoading,
      },
      (statsData?.total_transactions?.value || apiData?.total_transactions) && {
        id: 'total_txs' as const,
        icon: 'transactions_slim' as const,
        label: statsData?.total_transactions?.title || 'Total transactions',
        value: Number(statsData?.total_transactions?.value || apiData?.total_transactions).toLocaleString(),
        href: { pathname: '/txs' as const },
        isLoading,
      },
      statsData?.total_operational_transactions?.value && {
        id: 'total_operational_txs' as const,
        icon: 'transactions_slim' as const,
        label: statsData?.total_operational_transactions?.title || 'Total operational transactions',
        value: Number(statsData?.total_operational_transactions?.value).toLocaleString(),
        href: { pathname: '/txs' as const },
        isLoading,
      },
      apiData?.last_output_root_size && {
        id: 'latest_l1_state_batch' as const,
        icon: 'txn_batches_slim' as const,
        label: 'Latest L1 state batch',
        value: apiData?.last_output_root_size,
        href: { pathname: '/batches' as const },
        isLoading,
      },
      (statsData?.total_addresses?.value || apiData?.total_addresses) && {
        id: 'wallet_addresses' as const,
        icon: 'wallet' as const,
        label: statsData?.total_addresses?.title || 'Wallet addresses',
        value: Number(statsData?.total_addresses?.value || apiData?.total_addresses).toLocaleString(),
        isLoading,
      },
      hasGasTracker && apiData?.gas_prices && {
        id: 'gas_tracker' as const,
        icon: 'gas' as const,
        label: 'Gas tracker',
        value: apiData.gas_prices.average ? <GasPrice data={apiData.gas_prices.average} /> : 'N/A',
        hint: gasInfoTooltip,
        isLoading,
      },
      apiData?.rootstock_locked_btc && {
        id: 'btc_locked' as const,
        icon: 'coins/bitcoin' as const,
        label: 'BTC Locked in 2WP',
        value: `${BigNumber(apiData.rootstock_locked_btc).div(WEI).dp(0).toFormat()} RBTC`,
        isLoading,
      },
      apiData?.celo && {
        id: 'current_epoch' as const,
        icon: 'hourglass' as const,
        label: 'Current epoch',
        value: `#${apiData.celo.epoch_number}`,
        isLoading,
      },
    ]
      .filter(Boolean)
      .filter(({ id }) => config.UI.homepage.stats.includes(id));
  })();

  if (items.length === 0) {
    return null;
  }

  // 找到特定项目
  const totalTxsItem = items.find(item => item.id === 'total_txs');
  const totalBlocksItem = items.find(item => item.id === 'total_blocks');
  const gasTrackerItem = items.find(item => item.id === 'gas_tracker');
  const totalAddressesItem = items.find(item => item.id === 'wallet_addresses');

  // 日交易数据查询 - 使用与ChainIndicators类似的模式
  const statsDailyTxsQuery = useApiQuery('stats_main', {
    queryOptions: {
      refetchOnMount: false,
      enabled: isStatsFeatureEnabled,
      select: (data) => data?.daily_new_transactions?.chart.map((item) => ({
        date: new Date(item.date),
        value: Number(item.value)
      })) || [],
    },
  });

  const apiDailyTxsQuery = useApiQuery('stats_charts_txs', {
    queryOptions: {
      refetchOnMount: false,
      enabled: !isStatsFeatureEnabled,
      select: (data) => data.chart_data.map((item) => ({
        date: new Date(item.date),
        value: item.transaction_count
      })),
    },
  });

  // 创建图表数据
  const dailyTxsQuery = isStatsFeatureEnabled ? statsDailyTxsQuery : apiDailyTxsQuery;

  const dailyTxsData: TimeChartData = React.useMemo(() => {
    // 确保只使用最近14天的数据
    const last14DaysItems = (dailyTxsQuery.data || [])
      .slice(-14)
      .map(item => ({ ...item, value: Number(item.value) }))
      .filter(item => !isNaN(item.value));

    return [{
      items: last14DaysItems,
      name: 'Tx/day',
      valueFormatter: (x: number) => x.toLocaleString(undefined, { maximumFractionDigits: 0 }),
    }];
  }, [dailyTxsQuery.data]);

  return (
    <Grid
      templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
      gap={0}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      overflow="hidden"
      boxShadow={{
        _light: "0 0.5rem 1.2rem rgba(189, 197, 209, 0.2)",
        _dark: "0 0.5rem 1.2rem rgba(20, 20, 21, 0.4)"
      }}
      mb={6}
      w="100%"
      bg="white"
      _dark={{
        borderColor: "whiteAlpha.200",
        bg: "gray.800"
      }}
    >
      {/* 第一列：总交易数和总区块数 */}
      <GridItem>
        {/* 总交易数 */}
        {totalTxsItem && (
          <Box position="relative">
            <Flex
              p={3}
              m={0}
              borderBottom="1px solid"
              borderColor="gray.200"
              height="70px"
              alignItems="center"
              bg="transparent"
              _dark={{
                borderColor: "whiteAlpha.200",
                bg: "transparent"
              }}
            >
              <StatsWidget
                {...totalTxsItem}
                isLoading={isLoading}
              />
            </Flex>
            <Box
              position="absolute"
              right={0}
              top="15%"
              height="70%"
              width="1px"
              bg="gray.200"
              _dark={{ bg: "whiteAlpha.200" }}
            />
          </Box>
        )}

        {/* 总区块数 */}
        {totalBlocksItem && (
          <Box position="relative">
            <Flex
              p={3}
              m={0}
              height="70px"
              alignItems="center"
              bg="transparent"
              _dark={{ bg: "transparent" }}
            >
              <StatsWidget
                {...totalBlocksItem}
                isLoading={isLoading}
              />
            </Flex>
            <Box
              position="absolute"
              right={0}
              top="15%"
              height="70%"
              width="1px"
              bg="gray.200"
              _dark={{ bg: "whiteAlpha.200" }}
            />
          </Box>
        )}
      </GridItem>

      {/* 第二列：Gas跟踪器和总地址数 */}
      <GridItem>
        {/* Gas跟踪器 */}
        {gasTrackerItem && (
          <Box position="relative">
            <Flex
              p={3}
              m={0}
              borderBottom="1px solid"
              borderColor="gray.200"
              height="70px"
              alignItems="center"
              bg="transparent"
              _dark={{
                borderColor: "whiteAlpha.200",
                bg: "transparent"
              }}
            >
              <StatsWidget
                {...gasTrackerItem}
                isLoading={isLoading}
              />
            </Flex>
            <Box
              position="absolute"
              right={0}
              top="15%"
              height="70%"
              width="1px"
              bg="gray.200"
              _dark={{ bg: "whiteAlpha.200" }}
            />
          </Box>
        )}

        {/* 总地址数 */}
        {totalAddressesItem && (
          <Box position="relative">
            <Flex
              p={3}
              m={0}
              height="70px"
              alignItems="center"
              bg="transparent"
              _dark={{ bg: "transparent" }}
            >
              <StatsWidget
                {...totalAddressesItem}
                isLoading={isLoading}
              />
            </Flex>
            <Box
              position="absolute"
              right={0}
              top="15%"
              height="70%"
              width="1px"
              bg="gray.200"
              _dark={{ bg: "whiteAlpha.200" }}
            />
          </Box>
        )}
      </GridItem>

      {/* 第三列：日交易图表 */}
      <GridItem>
        <Flex
          direction="column"
          p={3}
          height="140px"
          bg="transparent"
          _dark={{ bg: "transparent" }}
        >
          <Flex align="center" mb={1}>
            <Text
              fontSize="xs"
              fontWeight="medium"
              color="#212529"
              _dark={{ color: "gray.400" }}
              mr={1}
            >
              TRANSACTION HISTORY IN 14 DAYS
            </Text>
            <Hint label="Number of transactions yesterday (0:00 - 23:59 UTC). The chart displays daily transactions for the past 14 days." />
          </Flex>



          {/* 图表组件 */}
          <Box
            flex="1"
            h="85px"
            position="relative"
            mt={-1}
          >
            <DailyTxChartContainer
              data={dailyTxsData}
              isError={dailyTxsQuery.isError}
              isPending={dailyTxsQuery.isPending}
            />
          </Box>
        </Flex>
      </GridItem>
    </Grid>
  );
};

export default Stats;
